import { useMemo } from "react";
import { useLocation } from "wouter";
import { useAuth } from "@/_core/hooks/useAuth";
import { trpc } from "@/lib/trpc";
import { buildRoleTurnSignals, type TurnSignal } from "@/lib/roleTurnSignals";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CalendarDays, Clock, CreditCard, Users, ArrowRight, WalletCards, CheckCircle2, CircleDollarSign, Loader2, SunMedium } from "lucide-react";
import { addDays, endOfDay, format, startOfDay } from "date-fns";

type SupportedRole = "caixa" | "recepcionista";

type RoleAction = {
  label: string;
  description: string;
  path: string;
  icon: typeof CalendarDays;
  tone: string;
};

const SIGNAL_ICONS = {
  confirmations: CalendarDays,
  payments: CircleDollarSign,
  agenda: Users,
  completed: CheckCircle2,
} as const;

const SIGNAL_TONES: Record<TurnSignal["tone"], string> = {
  amber: "border-amber-200 bg-amber-50 text-amber-800",
  emerald: "border-emerald-200 bg-emerald-50 text-emerald-800",
  blue: "border-blue-200 bg-blue-50 text-blue-800",
  violet: "border-violet-200 bg-violet-50 text-violet-800",
};

const ROLE_CONTENT: Record<SupportedRole, {
  title: string;
  badge: string;
  description: string;
  actions: RoleAction[];
}> = {
  recepcionista: {
    title: "Central da Recepção",
    badge: "Recepcionista",
    description: "Organize os atendimentos, acompanhe a agenda e registre recebimentos com rapidez.",
    actions: [
      { label: "Abrir agenda", description: "Criar e editar agendamentos", path: "/appointments", icon: CalendarDays, tone: "bg-amber-50 text-amber-700 border-amber-200" },
      { label: "Abrir fila", description: "Acompanhar chamadas e preparações", path: "/painel-chamada", icon: Users, tone: "bg-sky-50 text-sky-700 border-sky-200" },
      { label: "Registrar pagamento", description: "Receber atendimentos pendentes", path: "/checkout", icon: CreditCard, tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
      { label: "Ver clientes", description: "Consultar cadastro e histórico", path: "/clients", icon: Users, tone: "bg-blue-50 text-blue-700 border-blue-200" },
      { label: "Bater ponto", description: "Registrar entrada ou saída", path: "/clock-in", icon: Clock, tone: "bg-violet-50 text-violet-700 border-violet-200" },
    ],
  },
  caixa: {
    title: "Central do Caixa",
    badge: "Caixa",
    description: "Concentre-se nos pagamentos, na conferência dos atendimentos e no controle do turno.",
    actions: [
      { label: "Abrir caixa", description: "Pagamentos, vendas e saídas", path: "/checkout", icon: WalletCards, tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
      { label: "Ver agenda", description: "Localizar o atendimento do cliente", path: "/appointments", icon: CalendarDays, tone: "bg-amber-50 text-amber-700 border-amber-200" },
      { label: "Bater ponto", description: "Registrar entrada ou saída", path: "/clock-in", icon: Clock, tone: "bg-violet-50 text-violet-700 border-violet-200" },
    ],
  },
};

function formatArrivalIntentWindow(intent: { preferredDate: string; preferredTime?: string | null; preferredPeriod?: string | null }) {
  const [year, month, day] = intent.preferredDate.split("-");
  const dateLabel = year && month && day ? `${day}/${month}` : intent.preferredDate;
  if (intent.preferredTime) return `${dateLabel} · por volta de ${intent.preferredTime}`;
  const periodLabel = {
    manha: "pela manhã",
    tarde: "à tarde",
    noite: "à noite",
  }[intent.preferredPeriod ?? ""];
  return periodLabel ? `${dateLabel} · ${periodLabel}` : dateLabel;
}

export default function RoleDashboard({ role }: { role: SupportedRole }) {
  const { user } = useAuth();
  const [, navigate] = useLocation();
  const content = ROLE_CONTENT[role];
  const firstName = user?.name?.trim().split(" ")[0] || content.badge;
  const todayRange = useMemo(() => {
    const now = new Date();
    return { startDate: startOfDay(now), endDate: endOfDay(now) };
  }, []);
  const arrivalIntentRange = useMemo(() => {
    const today = new Date();
    return { startDate: format(today, "yyyy-MM-dd"), endDate: format(addDays(today, 1), "yyyy-MM-dd") };
  }, []);
  const liveQueryOptions = { refetchInterval: 30_000, refetchIntervalInBackground: true };
  const { data: todayAppointments, isLoading: loadingAppointments } = trpc.appointments.getByDateRange.useQuery(todayRange, liveQueryOptions);
  const { data: unpaidAppointments, isLoading: loadingPayments } = trpc.checkout.listUnpaid.useQuery({ period: "today" }, liveQueryOptions);
  const { data: mySalon } = trpc.salon.getMySalon.useQuery();
  const canViewTanningArrivalIntents = role === "recepcionista"
    && Number((mySalon as any)?.id) === 60002
    && (mySalon as any)?.tanningArrivalIntentsEnabled === true;
  const { data: tanningArrivalIntents = [], isLoading: loadingTanningArrivalIntents } = (trpc as any).queue.listTanningArrivalIntents.useQuery(
    arrivalIntentRange,
    { enabled: canViewTanningArrivalIntents, refetchInterval: 30_000, refetchIntervalInBackground: true },
  );
  const turn = useMemo(
    () => buildRoleTurnSignals(role, todayAppointments ?? [], unpaidAppointments?.length ?? 0),
    [role, todayAppointments, unpaidAppointments],
  );
  const PriorityIcon = SIGNAL_ICONS[turn.priority.key];

  return (
    <div className="space-y-5 md:space-y-6">
      <section className="rounded-2xl border border-primary/15 bg-gradient-to-br from-primary/10 via-background to-amber-50/60 p-4 sm:p-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <Badge variant="outline" className="mb-2 bg-background/80 text-primary border-primary/25">{content.badge}</Badge>
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">Minha operação agora</h1>
            <p className="mt-1 max-w-xl text-sm text-muted-foreground">Olá, {firstName}. {content.description}</p>
          </div>
          <Button onClick={() => navigate(turn.priority.path)} className="w-full sm:w-auto gap-2 shrink-0">
            Resolver prioridade <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </section>

      <Card className="border-primary/20 shadow-sm">
        <CardContent className="flex items-start gap-3 p-4">
          <div className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border ${SIGNAL_TONES[turn.priority.tone]}`}>
            <PriorityIcon className="h-4 w-4" />
          </div>
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Prioridade do turno</p>
            <p className="mt-0.5 text-sm font-semibold">{turn.priority.title}</p>
            <p className="mt-0.5 text-sm text-muted-foreground">{turn.priority.description}</p>
          </div>
        </CardContent>
      </Card>

      {canViewTanningArrivalIntents && (
        <Card className="border-amber-200 bg-amber-50/45 shadow-sm" data-testid="tanning-arrival-intents-card">
          <CardContent className="p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex min-w-0 items-start gap-3">
                <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-amber-200 bg-amber-100 text-amber-700">
                  <SunMedium className="h-4 w-4" />
                </span>
                <div className="min-w-0">
                  <p className="text-xs font-medium uppercase tracking-wide text-amber-800">Intenções de chegada — Bronze</p>
                  <p className="mt-0.5 text-sm font-semibold text-amber-950">Clientes que confirmaram que pretendem vir</p>
                </div>
              </div>
              <Badge variant="outline" className="shrink-0 border-amber-300 bg-background/70 text-amber-800">Sem reserva</Badge>
            </div>
            <p className="mt-3 text-xs leading-5 text-amber-900/75">A previsão ajuda a recepção a se preparar. Não ocupa horário na agenda e a cliente só entra na fila quando chegar presencialmente.</p>
            {loadingTanningArrivalIntents ? (
              <div className="mt-3 flex items-center gap-2 text-xs text-amber-900/65"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Atualizando previsões...</div>
            ) : tanningArrivalIntents.length === 0 ? (
              <p className="mt-3 rounded-lg border border-dashed border-amber-200 bg-background/60 p-3 text-sm text-amber-900/70">Nenhuma intenção de chegada confirmada para hoje ou amanhã.</p>
            ) : (
              <div className="mt-3 space-y-2" aria-live="polite">
                {tanningArrivalIntents.map((intent: any) => (
                  <div key={intent.id} className="flex items-center justify-between gap-3 rounded-lg border border-amber-200/80 bg-background/75 px-3 py-2.5">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-foreground">{intent.clientName}</p>
                      <p className="mt-0.5 truncate text-xs font-medium text-amber-900">Serviço: {intent.serviceNames?.join(" · ") || "Tipo de bronze não informado"}</p>
                      <p className="mt-0.5 text-xs text-muted-foreground">Previsão: {formatArrivalIntentWindow(intent)}</p>
                    </div>
                    <span className="shrink-0 text-xs font-medium text-amber-800">Confirmada</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <section>
        <div className="mb-3 flex items-center justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold">Sinais do turno</h2>
            <p className="text-xs text-muted-foreground">{loadingAppointments || loadingPayments ? "Atualizando pendências reais..." : `${turn.summary} Atualização automática a cada 30 segundos.`}</p>
          </div>
          {(loadingAppointments || loadingPayments) && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
        </div>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {turn.signals.map((signal) => {
            const Icon = SIGNAL_ICONS[signal.key];
            return (
              <button key={signal.key} type="button" onClick={() => navigate(signal.path)} className={`rounded-xl border p-3 text-left transition-colors hover:brightness-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${SIGNAL_TONES[signal.tone]}`}>
                <Icon className="h-4 w-4" />
                <p className="mt-2 text-xl font-semibold">{signal.count}</p>
                <p className="mt-1 line-clamp-2 text-xs font-medium">{signal.title}</p>
              </button>
            );
          })}
        </div>
      </section>

      <section>
        <div className="mb-3 flex items-center justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold">Ações do dia</h2>
            <p className="text-xs text-muted-foreground">Acessos liberados para o seu cargo.</p>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {content.actions.map((action) => {
            const Icon = action.icon;
            return (
              <button
                key={action.path}
                type="button"
                onClick={() => navigate(action.path)}
                className="group flex w-full items-center gap-3 rounded-xl border border-border bg-card p-4 text-left transition-colors hover:border-primary/35 hover:bg-accent/25 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border ${action.tone}`}>
                  <Icon className="h-5 w-5" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium">{action.label}</span>
                  <span className="mt-0.5 block text-xs text-muted-foreground">{action.description}</span>
                </span>
                <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
              </button>
            );
          })}
        </div>
      </section>
    </div>
  );
}
